/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Label, Row, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { t } from 'i18next';

import { CompanyId, JobRole, USER_ROLE } from '../../constants';
import LocalstorageService from '../../helpers/localstorage-service';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';

import SortableHeader from '../../components/SortableHeader';
import NoRecords from '../../components/Table/NoRecords';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import SpecialDaysService from '../../services/specialdays';
import { formatDate } from '../../helpers';

const HeaderList = [
  {
    title: '',  // Edit button column (RIGHT side in RTL)
    value: 'editAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
  {
    title: t('SPECIAL_DAYS.LOCATION'),
    value: 'locationName',
    style: { minWidth: '150px', width: '15%' },
  },
  {
    title: t('SPECIAL_DAYS.TERMINAL_NUMBER_LABEL'),
    value: 'terminalNumber',
    style: { minWidth: '150px', width: '15%' },
  },
  {
    title: t('SPECIAL_DAYS.REFERENCE_DAY_LABEL'),
    value: 'referenceDay',
    style: { minWidth: '150px', width: '15%' },
  },
  {
    title: t('SPECIAL_DAYS.FACTOR_TO_AVERAGE_LABEL'),
    value: 'factorToAverage',
    style: { minWidth: '150px', width: '20%' },
  },
  {
    title: t('SPECIAL_DAYS.NOTE_LABEL'),
    value: 'note',
    style: { minWidth: '350px', width: '60%' },
    sortable: false,
  },
  {
    title: '',  // Delete button column (LEFT side in RTL)
    value: 'deleteAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
];

const PAGE_SIZE_OPTIONS = [8, 10, 12, 15, 25];

function SpecialDaysList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [setPageTitle, setBreadcrumbItems] = useOutletContext();

  const [roleID, setRoleID] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState([]);

  const [deleteItem, setDeleteItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState([{ value: 'locationName', asc: true }]);

  const companyIdRef = useRef(null);

  useEffect(() => {
    setPageTitle(t('SPECIAL_DAYS.TITLE'));
    setBreadcrumbItems([
      { title: t('APPLICATION_TITLE'), link: '#' },
      { title: t('SPECIAL_DAYS.TITLE'), link: '#' },
    ]);

    companyIdRef.current = LocalstorageService.getItem(CompanyId);
    const jobRole = LocalstorageService.getItem(JobRole);
    setRoleID(jobRole);

    fetchList(1, 10, '', [{ value: 'locationName', asc: true }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchList(1, itemsPerPage, search, sortConfig);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function fetchList(pageNo, pageSize, searchTerm, sort) {
    const payload = {
      pageNo: pageNo,
      pageSize: pageSize,
      companyId: companyIdRef.current,
      sort: sort,
    };

    if (searchTerm) {
      payload.search = searchTerm;
    }

    setIsLoading(true);
    dispatch(setLoadingOverlay(true));

    try {
      const response = await SpecialDaysService.listSpecialDay(payload);
      const { status, message, dataList, totalCount } = response.data;

      if (status === 400) {
        toast.error(message);
        return;
      }

      setList(dataList || []);
      setTotalCount(totalCount || 0);
    } catch (error) {
      console.error('Error fetching special days:', error);
      toast.error('שגיאה בטעינת נתונים');
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  }

  const deleteModal = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const resetModal = () => {
    setDeleteItem(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    await SpecialDaysService.deleteMachine(deleteItem.id)
      .then((response) => {
        const { status, message } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        toast.success(message);
        fetchList(currentPage, itemsPerPage, search, sortConfig);
      })
      .finally(() => {
        setIsLoading(false);
      });

    resetModal();
  };

  const editFn = (item) => {
    navigate(`../update/${item.id}`, {
      state: { companyId: item.id },
    });
  };

  const sortBy = (sortObj) => {
    const newSort = sortObj ? [sortObj] : [{ value: 'locationName', asc: true }];
    setSortConfig(newSort);
    fetchList(currentPage, itemsPerPage, search, newSort);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeHandler = useCallback(debounce((e) => {
    setSearch(e.target.value?.trim() || '');
  }, 500), []);

  const handlePageClick = (page) => {
    setCurrentPage(page);
    fetchList(page, itemsPerPage, search, sortConfig);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setItemsPerPage(newSize);
    setCurrentPage(1);
    fetchList(1, newSize, search, sortConfig);
  };

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
          <FormGroup className="">
            <InputGroup>
              <Input onChange={debouncedChangeHandler} placeholder={t('SEARCH')} />
              <Button color="primary" type="button">
                <i className="ri-search-line"></i>
              </Button>
            </InputGroup>
          </FormGroup>
        </Form>

        <Button color="primary" className="float-end" onClick={() => navigate('../add')}>
          <span className="d-none d-sm-block">{t('SPECIAL_DAYS.ADD')}</span>
          <i className="ri-add-line d-block d-sm-none" title={t('SPECIAL_DAYS.ADD')}></i>
        </Button>
      </div>

      <Table striped bordered responsive size="sm">
        <thead>
          <tr>
            <SortableHeader
              headers={HeaderList}
              isDisabled={list.length === 0}
              sortClick={sortBy}
              showActionCol={false}
            />
          </tr>
        </thead>

        <tbody>
          {list.map((item, l0Idx) => (
            <tr key={l0Idx}>
              {/* Edit button - RIGHT side in RTL */}
              <td className="text-center">
                <Button color="primary" size="sm" title={t('SPECIAL_DAYS.UPDATE')} onClick={() => editFn(item)}>
                  {t('SPECIAL_DAYS.EDIT_BTN') || t('EDIT') || 'שנה'}
                </Button>
              </td>
              <td>{item.locationName}</td>
              <td>{item.terminalNumber}</td>
              <td>{formatDate(item.referenceDay, 'dd/mm/yyyy')}</td>
              <td>{Number(item.factorToAverage).toFixed(2)}</td>
              <td>{item.note}</td>
              {/* Delete button - LEFT side in RTL */}
              <td className="text-center">
                <Button color="danger" size="sm" title={t('SPECIAL_DAYS.DELETE')} onClick={() => deleteModal(item)}>
                  {t('SPECIAL_DAYS.DELETE_BTN') || 'לבטל'}
                </Button>
              </td>
            </tr>
          ))}

          {!isLoading && list.length === 0 && <NoRecords colSpan={HeaderList.length} />}
        </tbody>
      </Table>

      {/* Pagination and Rows per page */}
      <div className="custom-pagination mt-2 d-flex justify-content-between align-items-center flex-row-reverse">
        {/* Rows per page - RIGHT side in RTL */}
        <div className="d-flex align-items-center gap-2">
          <Label className="mb-0">{t('ROWS_PER_PAGE') || 'שורות לדף'}:</Label>
          <Input
            type="select"
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
            bsSize="sm"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Input>
        </div>

        {/* Pagination - LEFT side in RTL */}
        <PaginationControl
          page={currentPage}
          total={totalCount}
          limit={itemsPerPage}
          changePage={handlePageClick}
          next={true}
          last={true}
          ellipsis={1}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        isLoading={isLoading}
        confirmFn={confirmDelete}
        cancelFn={resetModal}
      />
    </React.Fragment>
  );
}

export default SpecialDaysList;